import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { logger } from 'client/utils/logger/logger';
import { Button } from 'reactstrap';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryModel, InventoryEntities } from 'client/data/models/inventory';
import { venomHistory } from 'client/utils/history/venom-history';
import { INVENTORY_TYPES_LOWERCASE } from 'client/constants/inventory-types';
import { SEE_INVENTORY_BY_PRICE } from 'site-modules/calculators/constants/tracking';
import { STORAGE_KEYS } from 'site-modules/shared/constants/car-buying';
import { Storage } from 'client/site-modules/shared/utils/storage';
import {
  DISPLAY_PRICE,
  INVENTORY_TYPE,
  ROOFTOP_ID,
  SORT_BY,
} from 'site-modules/shared/constants/allowed-seo-srp-request-params';
import { Link } from 'site-modules/shared/components/link/link';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getSearchFilterParamsWithDealerId } from 'site-modules/calculators/utils/affordability-calculator-utils';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';

const BUTTON_CTA = 'See Affordable Inventory';

export const CalculatorInventoryCardsUI = ({
  minPrice = 0,
  maxPrice = 0,
  createOrUpdateIdmData,
  downPayment = 0,
  tradeIn = 0,
  owedTradeIn = 0,
  vinsData = {},
  dealerId = null,
  setModelValue,
}) => {
  const [storage] = useState(new Storage('localStorage'));
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // mark it as not the first render anymore
      return;
    }
    setModelValue('searchFilter', InventoryModel, getSearchFilterParamsWithDealerId(minPrice, maxPrice, dealerId));
  }, [minPrice, maxPrice, setModelValue, dealerId]);

  const srpLink = useMemo(() => {
    const params = {
      [DISPLAY_PRICE]: `${parseInt(minPrice, 10)}-${parseInt(maxPrice, 10)}`,
    };

    if (dealerId) {
      params[ROOFTOP_ID] = dealerId;
      params[SORT_BY] = 'price:desc';
    } else {
      params[INVENTORY_TYPE] = INVENTORY_TYPES_LOWERCASE.NEW;
    }

    return srpLinkBuilder(params);
  }, [dealerId, maxPrice, minPrice]);

  const saveDataToIdm = async () => {
    const tradeInCalculated = tradeIn - owedTradeIn;

    if (tradeIn) {
      storage.set(STORAGE_KEYS.LEASE_PREFERENCES, {
        ...storage.get(STORAGE_KEYS.LEASE_PREFERENCES),
        downPayment,
        tradeIn: tradeInCalculated,
      });
      storage.set(STORAGE_KEYS.LOAN_PREFERENCES, {
        ...storage.get(STORAGE_KEYS.LOAN_PREFERENCES),
        downPayment,
        tradeIn: tradeInCalculated,
      });

      try {
        await createOrUpdateIdmData({
          idmDataToSave: {
            financing: {
              tradeInValuation: {
                tradeIn: tradeInCalculated,
              },
              leasePreferences: {
                downPayment,
              },
              loanPreferences: {
                downPayment,
              },
            },
          },
        });
      } catch (e) {
        logger.warn("Data wasn't saved properly", e);
      }
    }

    venomHistory.push(srpLink);
  };

  const { totalNumber } = vinsData;

  return (
    <LoadingSpinner
      isLoading={!Object.keys(vinsData).length}
      classes="text-gray-darker d-flex justify-content-between align-items-center flex-column-reverse flex-md-row"
    >
      <Link
        to={srpLink}
        className="text-blue-40 text-underline fw-bold"
        data-tracking-parent={SEE_INVENTORY_BY_PRICE}
        data-tracking-id={SEE_INVENTORY_BY_PRICE}
        data-tracking-value={totalNumber}
      >
        {totalNumber} Vehicles Available
      </Link>
      <div className="btn-wrapper text-end text-md-start">
        <Button
          color="success"
          onClick={saveDataToIdm}
          className="calculate-btn text-white rounded-pill px-1_5 py-1 mb-1_5 mb-md-0"
          {...(dealerId
            ? {
                'data-tracking-parent': SEE_INVENTORY_BY_PRICE,
                'data-tracking-id': SEE_INVENTORY_BY_PRICE,
                'data-tracking-value': BUTTON_CTA,
              }
            : {})}
        >
          {BUTTON_CTA}
        </Button>
      </div>
    </LoadingSpinner>
  );
};

CalculatorInventoryCardsUI.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  vinsData: PropTypes.shape({
    totalNumber: PropTypes.number,
    results: InventoryEntities.InventoriesVin,
  }),
  minPrice: PropTypes.number,
  maxPrice: PropTypes.number,
  title: PropTypes.string,
  dealerId: PropTypes.number,
  createOrUpdateIdmData: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  downPayment: PropTypes.number,
  tradeIn: PropTypes.number,
  owedTradeIn: PropTypes.number,
  hideInventoryCards: PropTypes.bool,
  renderVehiclesAvailableLink: PropTypes.bool,
};

export const stateToPropsConfig = {
  vinsData: bindToPath('vins', InventoryModel),
};

export const CalculatorInventoryCards = insiderMethods(connectToModel(CalculatorInventoryCardsUI, stateToPropsConfig));
